import { useMemo } from 'react';
import { Bookmark, Download, FolderPlusIcon, FilePlus2 } from 'lucide-react';
import {
  Button,
  Spinner,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import type { ProjectListItem } from '@/entities/project';
import type { OpportunityCardItem } from '../../../model/types';
import { SaveToProjectPopover, StartBriefPopover } from '@/shared/ui';
import { useSaveToProjectEnabled } from '@/shared/hooks';
import { useBookmarkToggle, useOpportunityPdfDownload } from '../../../hooks';
import { saveOpportunityArtifact } from '../../../api/opportunity.api';
import { useTranslation } from 'react-i18next';

interface OpportunityOverlayToolbarProps<T = unknown> {
  workspaceId: string | null | undefined;
  onStartBriefProjectSelected: (project: ProjectListItem) => void;
  bookmarkConfig?: {
    itemId: string;
    isFavorite: boolean;
    isLoading?: boolean;
    onToggle: (itemId: string, isFavorite: boolean) => Promise<void>;
  };
  currentItem?: T;
  showSaveToProject?: boolean;
}

/**
 * Renders the action toolbar for OpportunityDetailOverlay.
 * Displays buttons based on the provided configurations.
 * Each button includes a tooltip for better UX.
 */
export function OpportunityOverlayToolbar<T>({
  workspaceId,
  onStartBriefProjectSelected,
  bookmarkConfig,
  currentItem,
  showSaveToProject = false,
}: OpportunityOverlayToolbarProps<T>) {
  const { t } = useTranslation('analysis');

  const saveToProjectEnabled = useSaveToProjectEnabled();

  const { canDownload, download, isDownloading } = useOpportunityPdfDownload(
    workspaceId,
    currentItem as OpportunityCardItem | undefined,
  );

  // Bookmark toggle integration
  const bookmarkToggle = useBookmarkToggle({
    itemId: bookmarkConfig?.itemId ?? '',
    isFavorite: bookmarkConfig?.isFavorite ?? false,
    onToggle: bookmarkConfig?.onToggle ?? (async () => {}),
  });

  // Bookmark action config
  const bookmarkActionConfig = useMemo(
    () =>
      bookmarkConfig
        ? {
            isActive: bookmarkToggle.isBookmarked,
            isLoading: bookmarkConfig.isLoading ?? false,
            onClick: bookmarkToggle.handleToggle,
            ariaLabel: bookmarkToggle.isBookmarked
              ? t('opportunity.opportunityOverlayToolbar.removeFromRadar')
              : t('opportunity.opportunityOverlayToolbar.addToRadar'),
          }
        : undefined,
    [bookmarkConfig, bookmarkToggle, t],
  );

  const downloadActionConfig = useMemo(() => {
    if (!canDownload) return undefined;
    return {
      isLoading: isDownloading,
      isDisabled: isDownloading,
      onClick: () => void download(),
      ariaLabel: t('opportunity.opportunityOverlayToolbar.downloadOpportunity'),
    };
  }, [canDownload, isDownloading, download, t]);

  return (
    <div className="flex items-center gap-2">
      <TooltipProvider>
        {/* Bookmark button */}
        {bookmarkActionConfig && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="secondary"
                aria-label={bookmarkActionConfig.ariaLabel}
                onClick={bookmarkActionConfig.onClick}
                disabled={bookmarkActionConfig.isLoading}
              >
                {bookmarkActionConfig.isLoading ? (
                  <Spinner aria-hidden className="text-foreground" />
                ) : (
                  <Bookmark
                    className={`size-4 ${bookmarkActionConfig.isActive ? 'fill-accent-foreground text-accent-foreground' : 'text-foreground'}`}
                  />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>{bookmarkActionConfig.ariaLabel}</TooltipContent>
          </Tooltip>
        )}

        {/* Start Brief button */}
        <Tooltip>
          <StartBriefPopover
            workspaceId={workspaceId}
            onProjectSelected={onStartBriefProjectSelected}
          >
            <TooltipTrigger asChild>
              <Button
                aria-label={t('opportunity.opportunityOverlayToolbar.startBrief')}
                variant="secondary"
              >
                <FilePlus2 className="size-4 text-foreground" />
              </Button>
            </TooltipTrigger>
          </StartBriefPopover>
          <TooltipContent>{t('opportunity.opportunityOverlayToolbar.newBrief')}</TooltipContent>
        </Tooltip>

        {/* Download button */}
        {downloadActionConfig && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                aria-label={downloadActionConfig.ariaLabel}
                variant="secondary"
                onClick={downloadActionConfig.onClick}
                disabled={downloadActionConfig.isDisabled || downloadActionConfig.isLoading}
                className="disabled:opacity-40"
              >
                {downloadActionConfig.isLoading ? (
                  <Spinner aria-hidden className="text-foreground" />
                ) : (
                  <Download className="size-4 text-foreground" />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>{downloadActionConfig.ariaLabel}</TooltipContent>
          </Tooltip>
        )}

        {/* Save to Project button */}
        {showSaveToProject && currentItem && saveToProjectEnabled && (
          <SaveToProjectPopover
            resourceId={(currentItem as unknown as OpportunityCardItem).opportunityId}
            workspaceId={workspaceId}
            onMintArtifact={() =>
              saveOpportunityArtifact(
                workspaceId ?? '',
                currentItem as unknown as OpportunityCardItem,
              )
            }
          >
            <Button aria-label={t('opportunity.opportunityOverlayToolbar.saveToProject')}>
              {t('opportunity.opportunityOverlayToolbar.saveToProject')}
              <FolderPlusIcon className="size-4" />
            </Button>
          </SaveToProjectPopover>
        )}
      </TooltipProvider>
    </div>
  );
}
